import Long from 'long';
import {action, makeObservable, observable} from 'mobx';

import {fileUploader} from '../api/fileUploader';
import {NetworkResponse} from '../api/network';
import {IMCAttachment, MCAttachment, MCAttachmentSource} from '../api/proto';
import {AppStore} from '../stores/AppStore';
import {uint8ArrayToUuid} from '../utils/arrayUtils';
import FileData from '../utils/file/FileData';
import compressImage from '../utils/file/compressImages';
import getVideoPreviewImage from '../utils/file/getVideoPreviewImage';
import formatAttachment from '../utils/file/formatAttachment';
import AttachmentProgress, {IAttachmentProgress} from './Attachment/AttachmentProgress';

interface FilesUploadResponse {
  dataFiles: FileData[];
  attachments: IMCAttachment[];
  failedFiles: [string, FileData][];
}

export class UploadClient {
  constructor(private app: AppStore) {
    makeObservable(this);
  }

  @observable public progressMap: {[fileId: string]: AttachmentProgress} = {};

  private attachmentIDToString_ = (attachmentID?: Uint8Array | null): string => {
    if (!attachmentID) {
      return '';
    }
    return uint8ArrayToUuid(attachmentID);
  };

  public getFileState = (attachmentID?: Uint8Array | null): IAttachmentProgress | null => {
    const attachmentUuid = this.attachmentIDToString_(attachmentID);
    if (this.progressMap[attachmentUuid]) {
      return this.progressMap[attachmentUuid] || null;
    }
    return null;
  };

  public hasInProgress = (attachmentID?: Uint8Array | null): boolean => {
    const attachmentUuid = this.attachmentIDToString_(attachmentID);
    if (!attachmentID) {
      return false;
    }

    return !!this.progressMap[attachmentUuid];
  };

  public getFile = (attachmentID?: Uint8Array | null): FileData | null => {
    return this.getFileState(attachmentID)?.dataFile || null;
  };

  @action private addToProgress_ = (dataFile: FileData, progress: number) => {
    const attachmentUuid = this.attachmentIDToString_(dataFile.fileId);
    const progressInstance = this.progressMap[attachmentUuid];

    const data = {
      isUpload: true,
      isDone: false,
      inProgress: true,
      progress: progress,
      progressPercent: Math.floor((progress || 0) * 100),
      dataFile,
      abortController: new AbortController(),
      cancelable: true,
    };

    if (progressInstance) {
      progressInstance.fill(data);
    } else {
      this.progressMap[attachmentUuid] = new AttachmentProgress(data);
    }
  };

  @action private updateFileProgress_ = (attachmentID: Uint8Array, progress?: number | null, inProgress?: boolean) => {
    const progressInstance = this.getFileState(attachmentID);

    if (!progressInstance) {
      return;
    }

    if (progressInstance.progress !== progress) {
      console.debug(`%cUpload progress: ${this.attachmentIDToString_(attachmentID)} ${progress}`, 'color: gray');
      progressInstance.progress = progress || 0;
      progressInstance.progressPercent = Math.floor((progress || 0) * 100);
    }
    if (typeof inProgress === 'boolean') {
      progressInstance.inProgress = inProgress;
    }
  };

  @action private removeFromProgress_ = (attachmentID?: Uint8Array | null) => {
    const attachmentUuid = this.attachmentIDToString_(attachmentID);
    console.debug(`%cremove From Upload Progress: ${attachmentUuid}`, 'color: orange');

    if (this.progressMap[attachmentUuid]) {
      this.progressMap[attachmentUuid]?.fill({
        isDone: true,
        progress: 1,
        progressPercent: 100,
      });
      delete this.progressMap[attachmentUuid];
    }
  };

  @action public cancelUpload = (attachmentID?: Uint8Array | null) => {
    const attachmentUuid = this.attachmentIDToString_(attachmentID);
    console.debug(`%c cancel upload: ${attachmentUuid}`, 'color: orange');

    const progressInstance = this.progressMap[attachmentUuid];

    if (progressInstance) {
      if (progressInstance.dataFile) {
        progressInstance.dataFile.canceled = true;
      }
      progressInstance.isCanceled = true;
      progressInstance.abortController?.abort();

      this.removeFromProgress_(attachmentID);
    }
  };

  @action protected processUploadResult_ = (channelID: Uint8Array, dataFile: FileData, attachmentID: Uint8Array) => {
    dataFile.fileId = attachmentID;
    if (dataFile.attachment?.source?.reference) {
      dataFile.attachment.source.reference = attachmentID;
    }

    this.addToProgress_(dataFile, 1);

    this.app.activeWorkspace.attachmentStore.putToCache(channelID, dataFile, attachmentID);
  };

  protected isCanceled_ = (attachmentID?: Uint8Array | null): boolean => {
    const progressInstance = this.getFileState(attachmentID);

    return !progressInstance || !!progressInstance.isCanceled;
  };

  private uploadFile_ = async (dataFile: FileData, channelId?: Uint8Array | null): Promise<NetworkResponse<{attachmentID?: Uint8Array}>> => {
    const progressInstance = this.getFileState(dataFile.fileId);

    this.addToProgress_(dataFile, 0);

    const {error, res} = await fileUploader.upload({
      dataFile,
      channelId,
      signal: progressInstance?.abortController?.signal,
      onUploadProgress: ({progress}) => {
        this.updateFileProgress_(dataFile.fileId, progress);
      },
    });

    if (res?.attachmentID) {
      this.updateFileProgress_(dataFile.fileId, 1, false);
    }

    return {error, res};
  };

  private createVideoPreview_ = async (dataFile: FileData, channelId?: Uint8Array | null): Promise<FileData | null> => {
    const imageFile = dataFile.preview || (await getVideoPreviewImage(dataFile));

    const {res, error} = await this.uploadFile_(imageFile, channelId);

    if (error) {
      console.error(`%cError uploading video preview: ${error.message}`, 'color: red');
      return null;
    }

    if (res?.attachmentID) {
      imageFile.fileId = res?.attachmentID;
      imageFile.attachment = {
        type: MCAttachment.Type.IMAGE,
        source: {
          reference: res?.attachmentID,
          source: MCAttachmentSource.Source.BYTESERVER,
        },
        name: imageFile.fileName,
        mimeType: imageFile.mimeType || 'image/jpeg',
        width: Long.fromNumber(imageFile.width || 0),
        height: Long.fromNumber(imageFile.height || 0),
      };
    }

    return imageFile;
  };

  public uploadQueue = async (
    channelID: Uint8Array,
    dataFiles: FileData[],
    compressImages?: boolean,
    createPreview?: boolean,
  ): Promise<FilesUploadResponse> => {
    const filesCount: number = dataFiles.length;
    const attachmentIDs: Uint8Array[] = [];
    const uploadedDataFiles: FileData[] = [];
    const failedFiles: [string, FileData][] = [];

    if (filesCount) {
      for (let i = 0; i < filesCount; i++) {
        let dataFile = dataFiles[i];
        if (this.isCanceled_(dataFile.fileId)) {
          continue;
        }

        if (compressImages && dataFile.image) {
          dataFile = await compressImage(dataFile);
        }

        if (createPreview && dataFile.video && dataFile.attachment) {
          const previewImage = await this.createVideoPreview_(dataFile, channelID);
          dataFile.attachment.preview = previewImage?.attachment;
        }

        if (this.isCanceled_(dataFile.fileId)) {
          continue;
        }

        const {res, error} = await this.uploadFile_(dataFile, channelID);

        if (res?.attachmentID && this.getFileState(dataFile.fileId)) {
          this.processUploadResult_(channelID, dataFile, res?.attachmentID);

          attachmentIDs.push(res?.attachmentID);

          uploadedDataFiles.push(dataFile);
        } else if (res?.attachmentID && this.isCanceled_(dataFile.fileId)) {
          console.debug(`%c Upload canceled: ${this.attachmentIDToString_(dataFile.fileId)}`, 'color: red');
        } else {
          console.debug(
            `%c Upload error: ${error?.message} (${this.attachmentIDToString_(dataFile.fileId)})`,
            'color: red',
            error,
          );
          failedFiles.push([error?.message || '', dataFile]);
        }
      }
    }

    const attachments: IMCAttachment[] = [];
    uploadedDataFiles.forEach((dataFile) => {
      if (!dataFile.canceled && dataFile.attachment) {
        attachments.push(dataFile.attachment);
      }
    });

    const res = {
      failedFiles,
      dataFiles: uploadedDataFiles.filter((dataFile) => !this.isCanceled_(dataFile.fileId)),
      attachments,
    };

    attachmentIDs.forEach((attachmentID) => this.removeFromProgress_(attachmentID));

    return res;
  };

  public addToProgress = (dataFiles: FileData[]): void => {
    const filesCount: number = dataFiles.length;

    for (let i = 0; i < filesCount; i++) {
      const dataFile = dataFiles[i];

      this.addToProgress_(dataFile, 0);
    }
  };

  public upload = async (
    channelID: Uint8Array,
    dataFiles: FileData[],
    compressImages?: boolean,
    createPreview?: boolean,
  ): Promise<FilesUploadResponse> => {
    dataFiles.forEach((dataFile) => {
      dataFile.attachment = formatAttachment(dataFile);
    });
    this.addToProgress(dataFiles);

    return await this.uploadQueue(channelID, dataFiles, compressImages, createPreview);
  };
}

export default UploadClient;
